import React, { useCallback, useState } from "react";
import {
  Form,
  useFormValues,
  Submit,
  Select,
  Alert,
  Card,
  FutureArticleHeader,
  FormActions,
  Row,
  Col,
} from "@administrate/piston-ux";
import moment from "moment-timezone";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

import { findClosestTranslation } from "../../i18n";
import { useContentLocale } from "../../providers/ContentLocaleProvider";
import { Viewer } from "../../types/Viewer";
import { Maybe } from "../../types/Maybe";
import { useViewer } from "../../providers/ViewerProvider";

type PreferencesProps = {
  viewer: Viewer;
};

type ViewerUpdate = {
  locale?: Maybe<string>;
  timeZoneName?: Maybe<string>;
};

export const Preferences: React.FunctionComponent<
  React.PropsWithChildren<PreferencesProps>
> = ({ viewer }) => {
  const { t } = useTranslation();
  const { locales, contentLocale, setContentLocale } = useContentLocale();
  const initialValues: ViewerUpdate = {
    // The picker shows the Locale course content is shown in, which is what
    // it changes. The interface language follows the same choice below.
    locale: contentLocale?.code ?? viewer.locale,
    timeZoneName: viewer.timeZoneName,
  };
  const values = useFormValues(initialValues);
  const [updateViewerCalled, setUpdateViewerCalled] = useState(false);
  const {
    updateViewerMutation,
    updateViewerMutationIsLoading,
    updateViewerErrors,
  } = useViewer();

  const onSubmit = useCallback(
    async (formValues: any) => {
      setUpdateViewerCalled(true);
      const result = await updateViewerMutation({
        variables: formValues as ViewerUpdate,
      });
      if (result.errors && result.errors.length > 0) {
        // TODO handle error state for mutation
        console.log("error while saving preferfences");
        console.log(result.errors);
      } else {
        // The chosen Locale drives both languages. The interface switches to
        // that language's translations, staying in English when there are
        // none, while course content switches exactly as the retired nav bar
        // picker did.
        i18n.changeLanguage(findClosestTranslation(formValues.locale));

        const locale = locales.find(({ code }) => code === formValues.locale);
        // Switching to the current content Locale would rebuild the app for
        // nothing, which would also drop the success message below.
        if (locale && locale.id !== contentLocale?.id) {
          setContentLocale(locale);
        }
      }
    },
    [updateViewerMutation, locales, contentLocale, setContentLocale],
  );

  return (
    <div className="profile-preferences">
      <Form
        values={values}
        onSubmit={onSubmit}
        disabled={updateViewerMutationIsLoading}
      >
        <Card>
          <FutureArticleHeader title={t("preferences")} />
          {!updateViewerMutationIsLoading &&
            updateViewerCalled &&
            (!updateViewerErrors.length ? (
              <Alert
                type="success"
                message={t("Successful profile update")}
                overPage
              />
            ) : (
              <Alert type="error" message={t("Error occurred during update")} />
            ))}
          <Row>
            <Col sm={6}>
              <Select
                label={t("language")}
                options={
                  locales.length
                    ? locales.map((locale) => ({
                        label: locale.name,
                        value: locale.code,
                      }))
                    : // Without the instance's Locales there is nothing to
                      // translate course content into, so offer the interface
                      // languages only.
                      [
                        { label: t("english-us"), value: "en-US" },
                        { label: t("english-gb"), value: "en-GB" },
                        { label: t("french"), value: "fr" },
                        { label: t("dutch-be"), value: "nl-BE" },
                      ]
                }
                name="locale"
                valid={(v) => (!!v ? true : `${t("pleaseSelectValue")}`)}
              />
            </Col>
            <Col sm={6}>
              <Select
                label={t("timeZone")}
                options={moment.tz
                  .names()
                  .map((name) => ({ label: name, value: name }))}
                name="timeZoneName"
                valid={(v) => (!!v ? true : `${t("pleaseSelectValue")}`)}
              />
            </Col>
          </Row>
          <FormActions>
            {updateViewerMutationIsLoading && (
              <span
                className="glyphicon glyphicon-refresh glyphicon-spinner"
                style={{ marginRight: "5px" }}
              />
            )}
            <Submit
              label={t("updatePreferences")}
              inline
              disabled={updateViewerMutationIsLoading}
            />
          </FormActions>
        </Card>
      </Form>
    </div>
  );
};
